import { Box, Button, CircularProgress, Typography } from "@mui/material";
import DetailDialog from "common/components/DetailDialog";
import { useTranslation } from "common/locales";
import { getFormattedTime } from "common/utils/getFormattedTime";
import { useAnimalGroupMoveEvents } from "model";
import { Link } from "react-router-dom";
import { useAppStore } from "stores";

const ACCENT_COLOR = "#7D5DA3";
const NODE_SIZE = 12;
const CURRENT_NODE_SIZE = 16;
const LINE_COLOR = "#D3C9E7";

export function RotationTimelineButton({
  animalGroupId,
}: {
  animalGroupId: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      variant="outlined"
      color="primary"
      component={Link}
      to={`/animal-group/${animalGroupId}/rotations`}
      sx={{ width: "100%", mt: 1 }}
    >
      {t("animalGroup.viewRotations")}
    </Button>
  );
}

export function RotationTimelineDialog({
  open,
  close,
  animalGroupId,
  animalGroupName,
}: {
  open: boolean;
  close: () => void;
  animalGroupId: string;
  animalGroupName: string;
}) {
  const { t } = useTranslation();
  return (
    <DetailDialog
      open={open}
      onClose={close}
      title={`${t("rotationTimeline.title")} — ${animalGroupName}`}
    >
      {open && <RotationTimeline animalGroupId={animalGroupId} />}
    </DetailDialog>
  );
}

function RotationTimeline({ animalGroupId }: { animalGroupId: string }) {
  const { t } = useTranslation();
  const locale = useAppStore((state) => state.locale);
  const { data: moveEvents, isLoading } =
    useAnimalGroupMoveEvents(animalGroupId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!moveEvents || moveEvents.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("rotationTimeline.empty")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {moveEvents.map((event, index) => {
        const isCurrent = event.dateTimeOut === null;
        const isLast = index === moveEvents.length - 1;
        const fieldLabel = event.movedToShed
          ? t("rotationTimeline.shed")
          : event.fieldName ?? t("rotationTimeline.fieldDeleted");
        const dateIn = getFormattedTime(event.dateTimeIn, locale);
        const dateOut = event.dateTimeOut
          ? getFormattedTime(event.dateTimeOut, locale)
          : t("rotationTimeline.current");

        return (
          <Box key={event.moveNumber} sx={{ display: "flex", gap: 2 }}>
            {/* Timeline column */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 24,
                flexShrink: 0,
              }}
            >
              {/* Node */}
              <Box
                sx={{
                  width: isCurrent ? CURRENT_NODE_SIZE : NODE_SIZE,
                  height: isCurrent ? CURRENT_NODE_SIZE : NODE_SIZE,
                  borderRadius: "50%",
                  backgroundColor: isCurrent ? ACCENT_COLOR : LINE_COLOR,
                  border: isCurrent
                    ? `3px solid ${ACCENT_COLOR}`
                    : `2px solid ${ACCENT_COLOR}`,
                  flexShrink: 0,
                  mt: 0.5,
                }}
              />
              {/* Connector line */}
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    backgroundColor: LINE_COLOR,
                    minHeight: 16,
                  }}
                />
              )}
            </Box>

            {/* Content column */}
            <Box sx={{ flex: 1, pb: isLast ? 0 : 2, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isCurrent ? 700 : 500,
                    color: isCurrent ? ACCENT_COLOR : "#1a1a2e",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fieldLabel}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.7rem",
                    flexShrink: 0,
                    backgroundColor: isCurrent ? "#F3EEFA" : "#F5F5F5",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                  }}
                >
                  {t("rotationTimeline.move", {
                    number: event.moveNumber,
                  })}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: "0.7rem" }}
              >
                {dateIn} → {dateOut}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
